"use client";

import { useEffect, useRef, useState } from "react";
import { Building2, Search } from "lucide-react";
import type { Suggestion } from "@/modules/discovery/surfaces";
import { useDismissOnOutside } from "@/design/SearchChrome";

/**
 * Suggestions under the Service field as someone types (H56), the way a search
 * engine's box offers to finish the thought.
 *
 * **A suggestion is a shortcut, never a second search.** Picking a service puts
 * its category chip on, picking a clinic opens that clinic; pressing Enter with
 * nothing highlighted is still the one Search, unchanged (§12). So the list can
 * be ignored completely and the screen behaves exactly as it did.
 *
 * **The latest keystroke wins.** Each request is numbered and an answer to an
 * older one is dropped, so a slow reply to "ph" cannot replace the list for
 * "physio". A short pause before asking (150 ms) keeps a fast typist from
 * sending a request per letter.
 *
 * The input stays the caller's, uncontrolled, so `SearchControls` keeps reading
 * it live: this only listens to it.
 */
const DEBOUNCE_MS = 150;

export function KeywordSuggestions({
  inputRef,
  suggest,
  onPick,
  children,
}: {
  inputRef: React.RefObject<HTMLInputElement | null>;
  suggest: (typed: string) => Promise<Suggestion[]>;
  onPick: (suggestion: Suggestion) => void;
  children: React.ReactNode;
}) {
  const wrapper = useRef<HTMLDivElement>(null);
  const asked = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [items, setItems] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);

  const close = () => {
    setOpen(false);
    setActive(-1);
  };
  useDismissOnOutside(wrapper, close, open);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  function onInput() {
    const typed = inputRef.current?.value ?? "";
    if (timer.current) clearTimeout(timer.current);
    const mine = ++asked.current;
    if (typed.trim() === "") {
      setItems([]);
      close();
      return;
    }
    timer.current = setTimeout(() => {
      void suggest(typed).then(
        (found) => {
          if (mine !== asked.current) return;
          setItems(found);
          setActive(-1);
          setOpen(found.length > 0);
        },
        // A failed suggestion is silence, not an error: the field still
        // searches, which is all it ever promised.
        () => {
          if (mine === asked.current) close();
        },
      );
    }, DEBOUNCE_MS);
  }

  function pick(suggestion: Suggestion) {
    asked.current++;
    close();
    onPick(suggestion);
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if (!open || items.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive((i) => (i + 1) % items.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((i) => (i <= 0 ? items.length - 1 : i - 1));
    } else if (event.key === "Enter") {
      const chosen = items[active];
      if (chosen) {
        event.preventDefault();
        pick(chosen);
      } else {
        // Enter with nothing highlighted is the ordinary Search; the list
        // goes away so it is not left hanging over the results.
        asked.current++;
        close();
      }
    } else if (event.key === "Escape") {
      close();
    }
  }

  const services = items.filter((s) => s.kind === "category");
  const clinics = items.filter((s) => s.kind === "clinic");
  const indexOf = (s: Suggestion) => items.indexOf(s);

  return (
    <div ref={wrapper} className="relative" onInput={onInput} onKeyDown={onKeyDown}>
      {children}
      {open && (
        <ul
          role="listbox"
          aria-label="Suggestions"
          className="absolute inset-x-0 top-full z-40 mt-3 -ms-3 overflow-hidden rounded-xl border border-line bg-surface py-1.5 shadow-[var(--shadow-lift)] sm:w-[min(26rem,calc(100vw-3rem))]"
        >
          {services.length > 0 && <GroupLabel>Services</GroupLabel>}
          {services.map((s) => (
            <Row
              key={`c:${s.id}`}
              suggestion={s}
              active={indexOf(s) === active}
              onPick={pick}
              onHover={() => setActive(indexOf(s))}
            />
          ))}
          {clinics.length > 0 && <GroupLabel>Clinics</GroupLabel>}
          {clinics.map((s) => (
            <Row
              key={`b:${s.id}`}
              suggestion={s}
              active={indexOf(s) === active}
              onPick={pick}
              onHover={() => setActive(indexOf(s))}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function GroupLabel({ children }: { children: React.ReactNode }) {
  return (
    <li
      role="presentation"
      className="px-3 pb-1 pt-2 text-[11px] font-bold uppercase tracking-wide text-muted"
    >
      {children}
    </li>
  );
}

function Row({
  suggestion,
  active,
  onPick,
  onHover,
}: {
  suggestion: Suggestion;
  active: boolean;
  onPick: (s: Suggestion) => void;
  onHover: () => void;
}) {
  const Icon = suggestion.kind === "clinic" ? Building2 : Search;
  return (
    <li
      role="option"
      aria-selected={active}
      // Pointer down rather than click, so the field's blur cannot close the
      // list before the pick lands.
      onPointerDown={(e) => {
        e.preventDefault();
        onPick(suggestion);
      }}
      onPointerEnter={onHover}
      className={`flex cursor-pointer items-center gap-3 px-3 py-2 text-sm ${
        active ? "bg-accent/12 text-accent" : "text-body hover:bg-page"
      }`}
    >
      <Icon className="size-4 shrink-0 text-muted" aria-hidden strokeWidth={1.8} />
      <span className="truncate font-medium">{suggestion.name}</span>
    </li>
  );
}
